import type { WorkerAccessTeam, WorkerInfo } from "@rivets/core";

/** The `qk.workers` cache: `WorkersResponse`, with `teams` optional because a
 * list seeded locally (a registration before the first fetch) has none yet.
 * Every write keeps whatever else the entry holds, so a worker frame never
 * drops the teams the access editor reads. */
export interface WorkerListCache {
  workers: WorkerInfo[];
  teams?: WorkerAccessTeam[];
}

/** Applies a realtime `worker.updated` snapshot, or a worker returned by an
 * access or ownership change. The worker is replaced whole, never merged: a
 * snapshot whose `deliveryStall` is null or absent is how the server says a
 * stall has cleared, and the frame is tailored to this viewer, so its `canUse`
 * is the one to show. An unloaded list stays unloaded; the next fetch brings
 * the worker in. */
export function applyWorkerUpdate(
  current: WorkerListCache | undefined,
  updated: WorkerInfo,
): WorkerListCache | undefined {
  if (!current) return current;
  return current.workers.some((worker) => worker.id === updated.id)
    ? { ...current, workers: current.workers.map((worker) => worker.id === updated.id ? updated : worker) }
    : { ...current, workers: [...current.workers, updated] };
}

/** Realtime can publish the registered worker before the mutation response.
 * Keep that newer socket snapshot and never render a duplicate card. */
export function mergeRegisteredWorker(
  current: WorkerListCache | undefined,
  registered: WorkerInfo,
): WorkerListCache {
  if (!current) return { workers: [registered] };
  if (current.workers.some((worker) => worker.id === registered.id)) return current;
  return { ...current, workers: [registered, ...current.workers] };
}

/** A revoked worker leaves the list; the rest of the entry stays. */
export function removeWorker(current: WorkerListCache | undefined, workerId: string): WorkerListCache | undefined {
  return current ? { ...current, workers: current.workers.filter((worker) => worker.id !== workerId) } : current;
}
